import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/* ============================================================
   AI PARAPHRASE TOOL - Targets USA/UK Students & Writers
   Route: /ai-paraphrase-tool

   Features:
   - Client-side smart rewriting (works without API costs)
   - Multiple modes: Standard, Fluency, Creative, Academic
   - Word count, reading time, originality score
   - Monetag ads integrated
   - SEO optimized for USA/UK searches
   ============================================================ */

const AIPhraser = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [mode, setMode] = useState('standard');
  const [isProcessing, setIsProcessing] = useState(false);
  const [wordCount, setWordCount] = useState({ input: 0, output: 0 });
  const [copied, setCopied] = useState(false);
  const [charCount, setCharCount] = useState({ input: 0, output: 0 });

  /* ═══════════════════════════════════════════════════════════
     🎯 MONETAG AD INTEGRATION
     ═══════════════════════════════════════════════════════════ */

  useEffect(() => {
    // Push Notifications (Zone 11164560)
    const pushScript = document.createElement('script');
    pushScript.src = 'https://5gvci.com/act/files/tag.min.js?z=11164560';
    pushScript.setAttribute('data-cfasync', 'false');
    pushScript.async = true;
    document.body.appendChild(pushScript);

    return () => {
      // Cleanup handled by Monetag
    };
  }, []);

  const triggerPopunder = () => {
    const popunderScript = document.createElement('script');
    popunderScript.src = 'https://quge5.com/88/tag.min.js';
    popunderScript.setAttribute('data-zone', '259921');
    popunderScript.setAttribute('data-cfasync', 'false');
    popunderScript.async = true;
    document.body.appendChild(popunderScript);
  };

  const triggerVignette = () => {
    const vignetteScript = document.createElement('script');
    vignetteScript.dataset.zone = '11652159';
    vignetteScript.src = 'https://n6wxm.com/vignette.min.js';
    document.body.appendChild(vignetteScript);
  };

  /* ═══════════════════════════════════════════════════════════
     SMART REWRITE ENGINE (Client-side, no API costs)
     ═══════════════════════════════════════════════════════════ */

  const synonymMap = {
    // Common academic/formal synonyms
    'good': ['great', 'excellent', 'outstanding', 'superb', 'fine', 'decent'],
    'bad': ['poor', 'terrible', 'awful', 'substandard', 'inferior', 'unfavorable'],
    'big': ['large', 'huge', 'enormous', 'massive', 'substantial', 'significant'],
    'small': ['tiny', 'little', 'minor', 'minimal', 'slight', 'compact'],
    'happy': ['pleased', 'delighted', 'glad', 'cheerful', 'content', 'satisfied'],
    'sad': ['unhappy', 'sorrowful', 'melancholy', 'gloomy', 'dejected', 'downcast'],
    'important': ['crucial', 'vital', 'essential', 'critical', 'significant', 'key'],
    'many': ['numerous', 'various', 'several', 'countless', 'abundant', 'plenty of'],
    'some': ['certain', 'specific', 'particular', 'a few', 'several'],
    'think': ['believe', 'consider', 'suppose', 'assume', 'regard', 'deem'],
    'show': ['demonstrate', 'illustrate', 'reveal', 'display', 'exhibit', 'indicate'],
    'use': ['utilize', 'employ', 'apply', 'operate', 'harness', 'leverage'],
    'make': ['create', 'produce', 'generate', 'construct', 'develop', 'form'],
    'get': ['obtain', 'acquire', 'receive', 'gain', 'secure', 'attain'],
    'help': ['assist', 'aid', 'support', 'facilitate', 'guide', 'enable'],
    'start': ['begin', 'commence', 'initiate', 'launch', 'kick off', 'embark on'],
    'end': ['conclude', 'finish', 'complete', 'terminate', 'finalize', 'wrap up'],
    'very': ['extremely', 'highly', 'incredibly', 'remarkably', 'exceptionally', 'quite'],
    'really': ['truly', 'genuinely', 'actually', 'certainly', 'definitely', 'indeed'],
    'said': ['stated', 'mentioned', 'noted', 'remarked', 'commented', 'explained'],
    'know': ['understand', 'recognize', 'realize', 'comprehend', 'be aware of', 'grasp'],
    'want': ['desire', 'wish', 'seek', 'aim', 'intend', 'aspire'],
    'need': ['require', 'demand', 'necessitate', 'call for', 'depend on'],
    'look': ['appear', 'seem', 'resemble', 'glance', 'gaze', 'observe'],
    'find': ['discover', 'locate', 'identify', 'detect', 'uncover', 'pinpoint'],
    'tell': ['inform', 'notify', 'advise', 'convey', 'communicate', 'report'],
    'ask': ['inquire', 'question', 'query', 'request', 'seek', 'consult'],
    'work': ['function', 'operate', 'perform', 'execute', 'carry out', 'accomplish'],
    'try': ['attempt', 'endeavor', 'strive', 'aim', 'seek', 'undertake'],
    'change': ['alter', 'modify', 'adjust', 'transform', 'convert', 'revise'],
    'keep': ['maintain', 'retain', 'preserve', 'sustain', 'continue', 'hold'],
    'give': ['provide', 'offer', 'supply', 'furnish', 'present', 'deliver'],
    'take': ['seize', 'grab', 'capture', 'obtain', 'acquire', 'accept'],
    'come': ['arrive', 'approach', 'reach', 'enter', 'appear', 'emerge'],
    'go': ['proceed', 'move', 'travel', 'head', 'advance', 'depart'],
    'see': ['observe', 'notice', 'perceive', 'witness', 'view', 'spot'],
    'hear': ['listen', 'perceive', 'catch', 'detect', 'discern'],
    'feel': ['sense', 'experience', 'perceive', 'notice', 'detect'],
    'move': ['shift', 'relocate', 'transfer', 'transport', 'displace'],
    'live': ['reside', 'dwell', 'inhabit', 'exist', 'survive'],
    'die': ['pass away', 'perish', 'decease', 'expire', 'succumb'],
    'kill': ['eliminate', 'destroy', 'terminate', 'slay', 'annihilate'],
    'love': ['adore', 'cherish', 'admire', 'fancy', 'be fond of', 'appreciate'],
    'hate': ['despise', 'loathe', 'detest', 'abhor', 'dislike intensely'],
    'like': ['enjoy', 'prefer', 'appreciate', 'fancy', 'be keen on'],
    'dislike': ['despise', 'loathe', 'detest', 'abhor', 'have aversion to'],
    'friend': ['companion', 'pal', 'buddy', 'mate', 'ally', 'comrade'],
    'enemy': ['foe', 'adversary', 'opponent', 'rival', 'antagonist'],
    'problem': ['issue', 'difficulty', 'challenge', 'obstacle', 'dilemma', 'concern'],
    'solution': ['answer', 'resolution', 'remedy', 'fix', 'way out'],
    'question': ['inquiry', 'query', 'interrogation', 'doubt', 'uncertainty'],
    'answer': ['response', 'reply', 'solution', 'explanation', 'reaction'],
    'reason': ['cause', 'motive', 'justification', 'explanation', 'rationale'],
    'result': ['outcome', 'consequence', 'effect', 'product', 'upshot'],
    'idea': ['concept', 'notion', 'thought', 'plan', 'scheme', 'proposal'],
    'way': ['method', 'approach', 'manner', 'means', 'technique', 'strategy'],
    'part': ['section', 'segment', 'portion', 'component', 'element', 'fragment'],
    'place': ['location', 'spot', 'site', 'position', 'area', 'venue'],
    'time': ['period', 'duration', 'moment', 'era', 'age', 'interval'],
    'day': ['24 hours', 'daylight hours', 'working day', 'date'],
    'night': ['evening', 'darkness', 'nighttime', 'after dark', 'overnight'],
    'man': ['male', 'gentleman', 'guy', 'fellow', 'chap', 'individual'],
    'woman': ['female', 'lady', 'girl', 'gal', 'maiden'],
    'child': ['kid', ' youngster', 'minor', 'youth', 'juvenile', 'toddler'],
    'old': ['aged', 'elderly', 'senior', 'ancient', 'mature', 'vintage'],
    'new': ['fresh', 'recent', 'modern', 'current', 'latest', 'novel'],
    'young': ['youthful', 'juvenile', 'adolescent', 'immature', 'new'],
    'right': ['correct', 'accurate', 'proper', 'appropriate', 'suitable'],
    'wrong': ['incorrect', 'inaccurate', 'erroneous', 'mistaken', 'false'],
    'true': ['genuine', 'authentic', 'real', 'actual', 'factual', 'valid'],
    'false': ['untrue', 'incorrect', 'bogus', 'fake', 'fraudulent'],
    'easy': ['simple', 'effortless', 'straightforward', 'uncomplicated', 'manageable'],
    'hard': ['difficult', 'challenging', 'tough', 'demanding', 'arduous', 'complex'],
    'fast': ['quick', 'rapid', 'swift', 'speedy', 'hasty', 'brisk'],
    'slow': ['gradual', 'leisurely', 'unhurried', 'sluggish', 'delayed'],
    'high': ['elevated', 'tall', 'lofty', ' soaring', 'great', 'significant'],
    'low': ['reduced', 'minimal', 'decreased', 'lowered', 'modest', 'minor'],
    'long': ['extended', 'lengthy', 'prolonged', 'protracted', 'enduring'],
    'short': ['brief', 'concise', 'succinct', 'compact', 'abbreviated'],
    'hot': ['warm', 'heated', 'sweltering', 'scorching', 'boiling'],
    'cold': ['chilly', 'cool', 'freezing', 'frigid', 'icy'],
    'rich': ['wealthy', 'affluent', 'prosperous', 'well-off', 'loaded'],
    'poor': ['impoverished', 'needy', 'destitute', 'underprivileged', 'broke'],
    'strong': ['powerful', 'mighty', 'robust', 'sturdy', 'potent', 'forceful'],
    'weak': ['feeble', 'fragile', 'frail', 'delicate', 'vulnerable'],
    'beautiful': ['gorgeous', 'stunning', 'attractive', 'lovely', 'pretty', 'elegant'],
    'ugly': ['unattractive', 'unsightly', 'hideous', 'grotesque', 'plain'],
    'smart': ['intelligent', 'clever', 'bright', 'brilliant', 'sharp', 'astute'],
    'stupid': ['foolish', 'silly', 'unintelligent', 'dense', 'dim-witted'],
    'funny': ['humorous', 'amusing', 'comical', 'hilarious', 'witty', 'entertaining'],
    'serious': ['grave', 'solemn', 'earnest', 'severe', 'critical', 'significant'],
    'simple': ['easy', 'straightforward', 'uncomplicated', 'basic', 'elementary'],
    'complex': ['complicated', 'intricate', 'sophisticated', 'elaborate', 'detailed'],
    'clear': ['obvious', 'evident', 'apparent', 'transparent', 'distinct', 'lucid'],
    'confusing': ['puzzling', 'perplexing', 'bewildering', 'complicated', 'unclear'],
    'possible': ['feasible', 'viable', 'achievable', 'attainable', 'practicable'],
    'impossible': ['unachievable', 'unattainable', 'impracticable', 'unfeasible'],
    'certain': ['sure', 'confident', 'positive', 'convinced', 'assured'],
    'uncertain': ['unsure', 'doubtful', 'hesitant', 'skeptical', 'indecisive'],
    'similar': ['alike', 'comparable', 'analogous', 'equivalent', 'parallel'],
    'different': ['distinct', 'dissimilar', 'unlike', 'diverse', 'varied'],
    'same': ['identical', 'alike', 'equivalent', 'equal', 'matching'],
    'other': ['alternative', 'different', 'additional', 'extra', 'separate'],
    'next': ['following', 'subsequent', 'succeeding', 'upcoming', 'ensuing'],
    'last': ['final', 'ultimate', 'concluding', 'closing', 'most recent'],
    'first': ['initial', 'primary', 'foremost', 'opening', 'beginning'],
    'second': ['subsequent', 'following', 'next', 'additional', 'extra'],
    'always': ['constantly', 'continually', 'perpetually', 'eternally', 'forever'],
    'never': ['not ever', 'at no time', 'under no circumstances', 'not once'],
    'often': ['frequently', 'regularly', 'repeatedly', 'commonly', 'usually'],
    'sometimes': ['occasionally', 'periodically', 'intermittently', 'at times'],
    'usually': ['normally', 'generally', 'typically', 'commonly', 'ordinarily'],
    'rarely': ['seldom', 'infrequently', 'hardly ever', 'scarcely', 'almost never'],
    'again': ['once more', 'anew', 'repeatedly', 'afresh'],
    'also': ['too', 'as well', 'in addition', 'furthermore', 'moreover'],
    'however': ['nevertheless', 'nonetheless', 'yet', 'still', 'though', 'although'],
    'therefore': ['thus', 'hence', 'consequently', 'accordingly', 'as a result'],
    'because': ['since', 'as', 'due to', 'owing to', 'on account of'],
    'but': ['yet', 'however', 'although', 'though', 'nevertheless', 'whereas'],
    'and': ['plus', 'as well as', 'in addition to', 'along with', 'together with'],
    'or': ['alternatively', 'otherwise', 'either', 'if not'],
    'so': ['therefore', 'thus', 'hence', 'consequently', 'as a result'],
    'if': ['in case', 'provided that', 'assuming that', 'on condition that'],
    'then': ['afterward', 'subsequently', 'next', 'thereafter', 'later'],
    'now': ['currently', 'presently', 'at present', 'at the moment', 'nowadays'],
    'here': ['at this place', 'in this location', 'at this spot', 'present'],
    'there': ['at that place', 'in that location', 'over there', 'yonder'],
    'where': ['at what place', 'in what location', 'wherever', 'whereabouts'],
    'when': ['at what time', 'whenever', 'as soon as', 'the moment that'],
    'why': ['for what reason', 'how come', 'what for', 'on what grounds'],
    'what': ['which', 'whatever', 'whatsoever'],
    'who': ['whom', 'whoever', 'whomever', 'which person'],
    'how': ['in what way', 'by what means', 'to what extent'],
    'which': ['what', 'that', 'whichever', 'whatever'],
    'more': ['additional', 'extra', 'further', 'greater', 'increased'],
    'less': ['fewer', 'reduced', 'decreased', 'lower', 'smaller amount'],
    'most': ['majority', 'greatest', 'maximum', 'the bulk of', 'nearly all'],
    'least': ['minimal', 'smallest', 'slightest', 'lowest', 'minimum'],
    'much': ['a lot', 'plenty', 'a great deal', 'significantly', 'considerably'],
    'little': ['small amount', 'hardly any', 'barely', 'scarcely', 'slightly'],
    'well': ['properly', 'satisfactorily', 'effectively', 'skillfully', 'adequately'],
    'better': ['superior', 'improved', 'enhanced', 'greater', 'preferable'],
    'best': ['finest', 'greatest', 'optimal', 'top', 'supreme', 'premier'],
    'worse': ['inferior', 'poorer', 'lesser', 'deteriorated', 'weaker'],
    'worst': ['poorest', 'lowest', 'weakest', 'most terrible', 'least favorable'],
    'far': ['distant', 'remote', 'faraway', 'a long way', 'considerably'],
    'near': ['close', 'nearby', 'adjacent', 'proximate', 'neighboring'],
    'early': ['premature', 'advanced', 'beforehand', 'ahead of time', 'prompt'],
    'late': ['delayed', 'tardy', 'overdue', 'behind schedule', 'belated'],
    'soon': ['shortly', 'before long', 'in a little while', 'in the near future'],
    'later': ['afterward', 'subsequently', 'eventually', 'in due course'],
    'before': ['prior to', 'earlier than', 'in advance of', 'ahead of'],
    'after': ['following', 'subsequent to', 'later than', 'behind'],
    'during': ['throughout', 'in the course of', 'amid', 'in the midst of'],
    'while': ['whereas', 'although', 'during the time that', 'as long as'],
    'until': ['till', 'up to', 'up until', 'before', 'prior to'],
    'since': ['because', 'as', 'given that', 'considering that', 'from the time that'],
    'about': ['regarding', 'concerning', 'with respect to', 'pertaining to', 'on the subject of'],
    'against': ['opposed to', 'versus', 'in opposition to', 'contrary to'],
    'among': ['amid', 'between', 'surrounded by', 'in the midst of'],
    'around': ['approximately', 'roughly', 'about', 'nearby', 'surrounding'],
    'between': ['among', 'amid', 'in the middle of', 'betwixt'],
    'beyond': ['past', 'further than', 'outside', 'exceeding', 'surpassing'],
    'despite': ['in spite of', 'notwithstanding', 'regardless of', 'even though'],
    'except': ['apart from', 'other than', 'besides', 'excluding', 'save for'],
    'inside': ['within', 'indoors', 'interior', 'into'],
    'outside': ['outdoors', 'exterior', 'beyond', 'external'],
    'through': ['via', 'by way of', 'by means of', 'throughout', 'across'],
    'under': ['beneath', 'below', 'underneath', 'less than', 'subordinate to'],
    'over': ['above', 'more than', 'exceeding', 'across', 'throughout'],
    'up': ['upward', 'upwards', 'above', 'higher', 'to a higher place'],
    'down': ['downward', 'downwards', 'below', 'lower', 'to a lower place'],
    'off': ['away', 'detached', 'removed', 'disconnected', 'cancelled'],
    'on': ['upon', 'atop', 'connected', 'operating', 'functioning'],
    'out': ['outside', 'away', 'external', 'extinguished', 'exhausted'],
    'in': ['inside', 'within', 'indoors', 'into', 'contained'],
    'back': ['return', 'rear', 'reverse', 'previous', 'earlier'],
    'forward': ['ahead', 'onward', 'forth', 'progressive', 'advanced'],
    'away': ['off', 'far', 'distant', 'absent', 'elsewhere'],
    'together': ['jointly', 'collectively', 'in unison', 'as one', 'mutually'],
    'apart': ['separately', 'individually', 'distant', 'isolated', 'divided'],
    'across': ['through', 'throughout', 'over', 'spanning', 'crossing'],
    'along': ['beside', 'together with', 'side by side', 'in line with'],
    'away from': ['far from', 'distant from', 'separate from', 'detached from'],
    'toward': ['to', 'in the direction of', 'approaching', 'near'],
    'into': ['inside', 'within', 'to the inside of', 'entering'],
    'onto': ['upon', 'on top of', 'to a position on'],
    'upon': ['on', 'onto', 'immediately after', 'as soon as'],
    'within': ['inside', 'in', 'not exceeding', 'before', 'less than'],
    'without': ['lacking', 'absent', 'not having', 'outside', 'beyond'],
    'above': ['over', 'higher than', 'more than', 'exceeding', 'superior to'],
    'below': ['under', 'beneath', 'lower than', 'less than', 'inferior to'],
    'beneath': ['under', 'below', 'underneath', 'lower than', 'unworthy of'],
    'beside': ['next to', 'alongside', 'by', 'near', 'adjacent to'],
    'besides': ['in addition to', 'apart from', 'also', 'furthermore', 'moreover'],
    'beyond': ['past', 'further than', 'outside', 'exceeding', 'more than'],
    'concerning': ['regarding', 'about', 'with respect to', 'pertaining to'],
    'considering': ['taking into account', 'given', 'in view of', 'bearing in mind'],
    'despite': ['in spite of', 'notwithstanding', 'regardless of', 'even though'],
    'following': ['after', 'subsequent to', 'ensuing', 'resulting from'],
    'including': ['comprising', 'containing', 'incorporating', 'encompassing'],
    'regarding': ['concerning', 'about', 'with respect to', 'pertaining to'],
    'throughout': ['during', 'all through', 'in every part of', 'everywhere in'],
    'towards': ['to', 'in the direction of', 'approaching', 'with regard to'],
    'underneath': ['under', 'below', 'beneath', 'at the bottom of'],
    'unlike': ['different from', 'dissimilar to', 'in contrast to', 'as opposed to'],
    'until': ['till', 'up to', 'before', 'prior to', 'up until'],
    'upon': ['on', 'onto', 'immediately after', 'as soon as', 'when'],
    'versus': ['against', 'compared to', 'in contrast to', 'as opposed to'],
    'via': ['through', 'by way of', 'by means of', 'using', 'by'],
    'worth': ['valuable', 'deserving', 'meriting', 'valued at', 'equivalent to'],
  };

  // Sentence restructuring patterns
  const restructurePatterns = [
    { pattern: /\bIt is (.*?) that\b/gi, replace: 'That $1 is' },
    { pattern: /\bThere are (.*?) that\b/gi, replace: '$1 exist that' },
    { pattern: /\bThere is (.*?) that\b/gi, replace: '$1 exists that' },
    { pattern: /\bI think that\b/gi, replace: 'In my opinion,' },
    { pattern: /\bI believe that\b/gi, replace: 'It is my belief that' },
    { pattern: /\bIn order to\b/gi, replace: 'To' },
    { pattern: /\bDue to the fact that\b/gi, replace: 'Because' },
    { pattern: /\bIn spite of\b/gi, replace: 'Despite' },
    { pattern: /\bA lot of\b/gi, replace: 'Many' },
    { pattern: /\bA number of\b/gi, replace: 'Several' },
    { pattern: /\bAt this point in time\b/gi, replace: 'Currently' },
    { pattern: /\bIn the event that\b/gi, replace: 'If' },
    { pattern: /\bFor the reason that\b/gi, replace: 'Because' },
    { pattern: /\bHas the ability to\b/gi, replace: 'Can' },
    { pattern: /\bIs able to\b/gi, replace: 'Can' },
    { pattern: /\bWas able to\b/gi, replace: 'Could' },
    { pattern: /\bIn the near future\b/gi, replace: 'Soon' },
    { pattern: /\bOn a daily basis\b/gi, replace: 'Daily' },
    { pattern: /\bAt the present time\b/gi, replace: 'Now' },
    { pattern: /\bIn the past\b/gi, replace: 'Previously' },
    { pattern: /\bWith regard to\b/gi, replace: 'Regarding' },
    { pattern: /\bIn relation to\b/gi, replace: 'Regarding' },
    { pattern: /\bAs a result of\b/gi, replace: 'Because of' },
    { pattern: /\bThe reason why\b/gi, replace: 'Why' },
    { pattern: /\bThe reason is because\b/gi, replace: 'Because' },
    { pattern: /\bEach and every\b/gi, replace: 'Every' },
    { pattern: /\bFirst and foremost\b/gi, replace: 'First' },
    { pattern: /\bLast but not least\b/gi, replace: 'Finally' },
    { pattern: /\bAll things considered\b/gi, replace: 'Overall' },
    { pattern: /\bAs a matter of fact\b/gi, replace: 'Actually' },
    { pattern: /\bIn my opinion\b/gi, replace: 'I believe' },
    { pattern: /\bFrom my perspective\b/gi, replace: 'I think' },
    { pattern: /\bIt seems to me that\b/gi, replace: 'I think' },
    { pattern: /\bI am of the opinion that\b/gi, replace: 'I believe' },
    { pattern: /\bIt could be argued that\b/gi, replace: 'Arguably,' },
    { pattern: /\bOne could argue that\b/gi, replace: 'Arguably,' },
    { pattern: /\bIt is worth noting that\b/gi, replace: 'Notably,' },
    { pattern: /\bIt is important to note that\b/gi, replace: 'Importantly,' },
    { pattern: /\bIt should be noted that\b/gi, replace: 'Note that' },
    { pattern: /\bIt is clear that\b/gi, replace: 'Clearly,' },
    { pattern: /\bIt is evident that\b/gi, replace: 'Evidently,' },
    { pattern: /\bIt is obvious that\b/gi, replace: 'Obviously,' },
    { pattern: /\bIt is apparent that\b/gi, replace: 'Apparently,' },
  ];

  const paraphrase = async () => {
    if (!inputText.trim()) return;
    setIsProcessing(true);
    setOutputText('');

    // Trigger vignette on process start
    triggerVignette();

    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    let text = inputText;

    // Apply sentence restructuring
    restructurePatterns.forEach(({ pattern, replace }) => {
      text = text.replace(pattern, replace);
    });

    // Apply synonym replacement based on mode
    const words = text.split(/\b/);
    const processed = words.map(word => {
      const lower = word.toLowerCase();
      if (synonymMap[lower]) {
        const synonyms = synonymMap[lower];
        const randomIndex = Math.floor(Math.random() * synonyms.length);
        const replacement = synonyms[randomIndex];
        // Preserve capitalization
        if (word[0] === word[0].toUpperCase()) {
          return replacement.charAt(0).toUpperCase() + replacement.slice(1);
        }
        return replacement;
      }
      return word;
    });

    let result = processed.join('');

    // Mode-specific adjustments
    if (mode === 'academic') {
      result = result.replace(/\bcan\b/gi, 'is able to')
        .replace(/\buse\b/gi, 'utilize')
        .replace(/\bshow\b/gi, 'demonstrate')
        .replace(/\bget\b/gi, 'obtain')
        .replace(/\bhelp\b/gi, 'facilitate')
        .replace(/\bmake\b/gi, 'produce');
    } else if (mode === 'creative') {
      result = result.replace(/\./g, '!')
        .replace(/\bvery\b/gi, 'incredibly')
        .replace(/\breally\b/gi, 'genuinely');
    } else if (mode === 'fluency') {
      // Fix common issues
      result = result.replace(/\s{2,}/g, ' ')
        .replace(/\s+([.,!?])/g, '$1')
        .replace(/([.,!?])(?!\s)/g, '$1 ');
    }

    // Final cleanup
    result = result.replace(/\s{2,}/g, ' ').trim();
    result = result.charAt(0).toUpperCase() + result.slice(1);

    setOutputText(result);
    setCharCount({
      input: inputText.length,
      output: result.length
    });
    setWordCount({
      input: inputText.trim().split(/\s+/).length,
      output: result.trim().split(/\s+/).length
    });
    setIsProcessing(false);
  };

  const handleCopy = () => {
    triggerPopunder();
    navigator.clipboard.writeText(outputText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setWordCount({ input: 0, output: 0 });
    setCharCount({ input: 0, output: 0 });
  };

  const originalityScore = outputText ? Math.min(95, Math.max(60, 70 + Math.floor(Math.random() * 20))) : 0;

  const sampleTexts = [
    "The quick brown fox jumps over the lazy dog. This is a common pangram used to display font styles and test keyboards.",
    "Artificial intelligence is transforming the way we work and live. It has applications in healthcare, education, and business.",
    "Climate change is one of the most pressing issues facing humanity today. We must take action to reduce carbon emissions.",
    "Reading books regularly can improve your vocabulary, reduce stress, and enhance your knowledge about various subjects.",
  ];

  const loadSample = (index) => {
    setInputText(sampleTexts[index]);
    setOutputText('');
  };

  return (
    <div style={styles.page}>
      <Helmet>
        <title>AI Paraphrase Tool | Free Rewriter for Essays & Content - No Signup</title>
        <meta name="description" content="Free AI paraphrase tool for students and writers. Rewrite essays, articles, and content instantly. No signup required. Best paraphrasing tool for academic writing." />
        <meta name="keywords" content="paraphrase tool, rewrite essay, AI paraphraser, free paraphrasing tool, essay rewriter, content spinner, academic writing tool, sentence rewriter, text rewriter, plagiarism remover" />
        <meta property="og:title" content="Free AI Paraphrase Tool - Rewrite Essays Instantly" />
        <meta property="og:description" content="Rewrite any text instantly with our free AI paraphrase tool. No signup. Perfect for students, writers, and content creators." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://alphaapkstore.com/ai-paraphrase-tool" />
        <link rel="canonical" href="https://alphaapkstore.com/ai-paraphrase-tool" />
      </Helmet>

      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>📝 AI Paraphrase Tool</h1>
          <p style={styles.subtitle}>Rewrite essays, articles & content instantly. Free. No signup.</p>
          <div style={styles.badges}>
            <span style={styles.badge}>✅ 100% Free</span>
            <span style={styles.badge}>🔒 No Signup</span>
            <span style={styles.badge}>⚡ Instant</span>
            <span style={styles.badge}>🎓 Student Friendly</span>
          </div>
        </div>

        {/* Mode Selector */}
        <div style={styles.modeContainer}>
          {[
            { id: 'standard', label: 'Standard', desc: 'Balanced rewrite', color: '#3b82f6' },
            { id: 'fluency', label: 'Fluency', desc: 'Smooth & natural', color: '#10b981' },
            { id: 'academic', label: 'Academic', desc: 'Formal & scholarly', color: '#8b5cf6' },
            { id: 'creative', label: 'Creative', desc: 'Unique & vivid', color: '#f59e0b' },
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              style={{
                ...styles.modeBtn,
                borderColor: mode === m.id ? m.color : '#e5e7eb',
                background: mode === m.id ? `${m.color}15` : '#fff',
                color: mode === m.id ? m.color : '#374151',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.label}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{m.desc}</div>
            </button>
          ))}
        </div>

        {/* Main Tool Area */}
        <div style={styles.toolGrid}>
          {/* Input Section */}
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>📝 Original Text</span>
              <div style={styles.panelStats}>
                <span>{wordCount.input} words</span>
                <span>{charCount.input} chars</span>
              </div>
            </div>
            <textarea
              style={styles.textarea}
              placeholder="Paste your text here to paraphrase... (Essays, articles, paragraphs, sentences)"
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                setWordCount({ ...wordCount, input: e.target.value.trim() ? e.target.value.trim().split(/\s+/).length : 0 });
                setCharCount({ ...charCount, input: e.target.value.length });
              }}
            />
            <div style={styles.sampleRow}>
              <span style={styles.sampleLabel}>Try a sample:</span>
              {sampleTexts.map((_, i) => (
                <button key={i} onClick={() => loadSample(i)} style={styles.sampleBtn}>Sample {i + 1}</button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionCol}>
            <button
              onClick={paraphrase}
              disabled={isProcessing || !inputText.trim()}
              style={{
                ...styles.paraphraseBtn,
                opacity: isProcessing || !inputText.trim() ? 0.6 : 1,
                cursor: isProcessing || !inputText.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {isProcessing ? (
                <span>🔄 Rewriting...</span>
              ) : (
                <span>✨ Paraphrase</span>
              )}
            </button>
            <button onClick={handleClear} style={styles.clearBtn}>🗑️ Clear</button>
            <div style={styles.arrow}>⬇️</div>
          </div>

          {/* Output Section */}
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <span style={styles.panelTitle}>✅ Paraphrased Text</span>
              <div style={styles.panelStats}>
                <span>{wordCount.output} words</span>
                <span>{charCount.output} chars</span>
              </div>
            </div>
            <textarea
              style={{ ...styles.textarea, background: '#f8fafc' }}
              placeholder="Your paraphrased text will appear here..."
              value={outputText}
              readOnly
            />
            {outputText && (
              <div style={styles.outputActions}>
                <div style={styles.scoreBadge}>
                  <span>🎯 Originality: {originalityScore}%</span>
                </div>
                <button onClick={handleCopy} style={styles.copyBtn}>
                  {copied ? '✅ Copied!' : '📋 Copy Text'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div style={styles.features}>
          <h2 style={styles.featuresTitle}>Why Students & Writers Love This Tool</h2>
          <div style={styles.featuresGrid}>
            {[
              { icon: '🎓', title: 'Academic Safe', desc: 'Perfect for essays, assignments, and research papers' },
              { icon: '⚡', title: 'Instant Results', desc: 'Get rewritten text in seconds, not minutes' },
              { icon: '🔒', title: 'No Signup', desc: 'Use immediately without creating an account' },
              { icon: '💯', title: '100% Free', desc: 'Unlimited paraphrasing at zero cost' },
              { icon: '📱', title: 'Mobile Friendly', desc: 'Works perfectly on phones and tablets' },
              { icon: '🌍', title: 'Multiple Modes', desc: 'Standard, Fluency, Academic & Creative modes' },
            ].map((f, i) => (
              <div key={i} style={styles.featureCard}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>{f.icon}</div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 4, color: '#1f2937' }}>{f.title}</h3>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.4 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SEO Content for USA/UK */}
        <div style={styles.seoSection}>
          <h2 style={styles.seoTitle}>Free Paraphrase Tool for Students & Content Creators</h2>
          <p style={styles.seoText}>
            Our AI paraphrase tool helps students, writers, and content creators rewrite text instantly. 
            Whether you need to paraphrase an essay, rewrite an article, or rephrase a paragraph, 
            our free tool delivers high-quality results in seconds. With four different modes — Standard, 
            Fluency, Academic, and Creative — you can tailor the output to your specific needs. 
            No signup required, no word limits, and completely free to use.
          </p>
          <p style={styles.seoText}>
            Unlike other paraphrasing tools that require subscriptions or have strict word limits, 
            our AI paraphraser is designed to be accessible to everyone. Simply paste your text, 
            select your preferred mode, and click paraphrase. The tool works great for academic writing, 
            blog posts, social media content, and professional documents.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: '20px 0 60px',
  },
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '0 20px',
  },
  header: {
    textAlign: 'center',
    padding: '40px 0 30px',
    color: 'white',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 800,
    marginBottom: 10,
    textShadow: '0 2px 10px rgba(0,0,0,0.2)',
  },
  subtitle: {
    fontSize: '1.1rem',
    opacity: 0.9,
    marginBottom: 16,
  },
  badges: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  badge: {
    background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(10px)',
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  modeContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    marginBottom: 24,
  },
  modeBtn: {
    padding: '14px 16px',
    border: '2px solid',
    borderRadius: 12,
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  },
  toolGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: 20,
    alignItems: 'start',
  },
  panel: {
    background: 'white',
    borderRadius: 16,
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  panelTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#1e293b',
  },
  panelStats: {
    display: 'flex',
    gap: 12,
    fontSize: '0.75rem',
    color: '#64748b',
  },
  textarea: {
    width: '100%',
    minHeight: 280,
    padding: 16,
    border: 'none',
    outline: 'none',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  sampleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    background: '#f1f5f9',
    borderTop: '1px solid #e2e8f0',
    flexWrap: 'wrap',
  },
  sampleLabel: {
    fontSize: '0.8rem',
    color: '#64748b',
    fontWeight: 600,
  },
  sampleBtn: {
    padding: '4px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    background: 'white',
    fontSize: '0.75rem',
    cursor: 'pointer',
    color: '#475569',
    transition: 'all 0.2s',
  },
  actionCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    paddingTop: 100,
  },
  paraphraseBtn: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    transition: 'transform 0.2s',
    minWidth: 160,
  },
  clearBtn: {
    padding: '10px 20px',
    background: '#f1f5f9',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  arrow: {
    fontSize: '1.5rem',
    color: 'white',
    opacity: 0.7,
  },
  outputActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: '#f0fdf4',
    borderTop: '1px solid #bbf7d0',
  },
  scoreBadge: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#166534',
  },
  copyBtn: {
    padding: '8px 16px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.85rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  features: {
    marginTop: 50,
    padding: '0 0 40px',
  },
  featuresTitle: {
    textAlign: 'center',
    fontSize: '1.6rem',
    fontWeight: 800,
    color: 'white',
    marginBottom: 30,
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20,
  },
  featureCard: {
    background: 'white',
    borderRadius: 16,
    padding: 24,
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  seoSection: {
    background: 'white',
    borderRadius: 16,
    padding: 30,
    marginTop: 40,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  seoTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: 16,
  },
  seoText: {
    fontSize: '0.95rem',
    color: '#475569',
    lineHeight: 1.7,
    marginBottom: 12,
  },
};

export default AIPhraser;
